'use client';

import Alert from '@enlighten/design-system/dist/MuiAlert';
import Snackbar from '@enlighten/design-system/dist/Snackbar';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useState } from 'react';

interface Message {
	type: 'success' | 'info' | 'warning' | 'error';
	message: string;
	duration?: number;
}

const messageContext = createContext<Message | null | undefined>(null);
const onChangeMessageContext = createContext<(value: Message | null | undefined) => void>(() => {});

export function useMessage() {
	return useContext(messageContext);
}

/**
 * @function useOnChangeMessage Alert 메시지 노출 함수
 * @argument type `'success' | 'info' | 'warning' | 'error'`
 * @argument message Alert 메시지 내용
 * @argument duration Alert 메시지 노출 시간
 */
export function useOnChangeMessage() {
	return useContext(onChangeMessageContext);
}

/** Alert 메시지 Provider */
export default function MessageProvider({ children }: PropsWithChildren) {
	const [message, setMessage] = useState<Message | null | undefined>();

	return (
		<messageContext.Provider value={message}>
			<onChangeMessageContext.Provider value={setMessage}>
				{children}
				<Snackbar
					open={!!message}
					anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
					autoHideDuration={message?.duration ?? 3000}
					onClose={(_, reason) => {
						if (reason === 'timeout') {
							setMessage(undefined);
						}
					}}
					sx={{
						top: { xs: 'calc(24px + 64px)', md: 'calc(24px + 96px)' },
						zIndex: 999999,
						'.MuiPaper-root': {
							bgcolor: 'common.white',
						},
					}}
				>
					<Alert severity={message?.type ?? 'info'} variant="outlined" title={`alert message ${message?.type ?? ''}`}>
						{message?.message}
					</Alert>
				</Snackbar>
			</onChangeMessageContext.Provider>
		</messageContext.Provider>
	);
}
